// src/Component/Pages/SuperAdmin/CollegeManagement.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineOfficeBuilding,
  HiOutlineSearch,
  HiOutlineRefresh,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineBan,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUser,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineGlobeAlt,
  HiOutlineLocationMarker,
  HiOutlineClock
} from 'react-icons/hi';
import { superAdminService } from '../../../superAdminService';
import Header from '../../SuperAdmin/Header';
import Sidebar from '../../SuperAdmin/Sidebar';

const CollegeManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    suspended: 0
  });

  const searchInputRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter colleges based on search and status
  useEffect(() => {
    let filtered = [...colleges];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(college => college.status === statusFilter);
    }
    
    if (debouncedSearchTerm.trim() !== '') {
      filtered = filtered.filter(
        (college) =>
          college.collegeName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          college.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          college.adminName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          college.contactNumber?.includes(debouncedSearchTerm)
      );
    }
    
    setFilteredColleges(filtered);
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, colleges]);

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const response = await superAdminService.getColleges();
      
      if (response.success) {
        const collegesData = response.data || [];
        const normalizedColleges = collegesData.map(college => ({
          ...college,
          id: college.id || college._id,
          _id: college._id || college.id,
          createdAt: college.createdAt || new Date().toISOString()
        }));
        
        setColleges(normalizedColleges);
        setFilteredColleges(normalizedColleges);
        
        const stats = {
          total: normalizedColleges.length,
          pending: normalizedColleges.filter(c => c.status === 'pending').length,
          approved: normalizedColleges.filter(c => c.status === 'approved').length,
          rejected: normalizedColleges.filter(c => c.status === 'rejected').length,
          suspended: normalizedColleges.filter(c => c.status === 'suspended').length
        };
        setStats(stats);
      }
    } catch (error) {
      console.error('Error fetching colleges:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSuspend = async () => {
    if (!selectedCollege || !suspendReason.trim()) return;
    
    setProcessingId(selectedCollege._id);
    try {
      const response = await superAdminService.suspendCollege(selectedCollege._id, suspendReason);
      
      if (response.success) {
        setColleges(prev => prev.map(c => 
          c._id === selectedCollege._id 
            ? { ...c, status: 'suspended', suspendReason, suspendedAt: new Date() }
            : c
        ));
        setShowSuspendModal(false);
        setSelectedCollege(null);
        setSuspendReason('');
      }
    } catch (error) {
      console.error('Error in handleSuspend:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnsuspend = async (collegeId) => {
    setProcessingId(collegeId);
    try {
      const response = await superAdminService.unsuspendCollege(collegeId);
      if (response.success) {
        setColleges(prev => prev.map(c => 
          c._id === collegeId ? { ...c, status: 'approved', suspendReason: null } : c
        ));
      }
    } catch (error) {
      console.error('Error unsuspending college:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (collegeId) => {
    if (!window.confirm('Are you sure you want to delete this college? This action cannot be undone.')) return;
    
    setProcessingId(collegeId);
    try {
      const response = await superAdminService.deleteCollege(collegeId);
      if (response.success) {
        setColleges(prev => prev.filter(c => c._id !== collegeId));
      }
    } catch (error) {
      console.error('Error deleting college:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending', icon: HiOutlineClock },
      approved: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Approved', icon: HiOutlineCheck },
      rejected: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Rejected', icon: HiOutlineX },
      suspended: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Suspended', icon: HiOutlineBan }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 ${config.bg} ${config.text} rounded-full text-xs font-medium whitespace-nowrap`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Pagination
  const totalPages = Math.ceil(filteredColleges.length / itemsPerPage);
  const paginatedColleges = filteredColleges.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Skeleton loader
  const StatsCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
      <div className="h-6 bg-gray-300 rounded w-12"></div>
    </div>
  );

  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          <div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </td>
      <td className="px-4 py-3">
        <div className="h-6 bg-gray-200 rounded-full w-20"></div>
      </td>
      <td className="px-4 py-3">
        <div className="flex space-x-1">
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
          <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
        </div>
      </td>
    </tr>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 bg-white rounded-xl border border-gray-200"
    >
      <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <HiOutlineOfficeBuilding className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No Colleges Found</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {searchTerm || statusFilter !== 'all' 
          ? 'No colleges match your current filters. Try adjusting your search criteria.'
          : 'There are no colleges registered yet.'}
      </p>
      {(searchTerm || statusFilter !== 'all') && (
        <button
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
            searchInputRef.current?.focus();
          }}
          className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition inline-flex items-center gap-2"
        >
          <HiOutlineX className="w-5 h-5" />
          Clear Filters
        </button>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="College Management" toggleSidebar={toggleSidebar} />
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {[...Array(5)].map((_, i) => <StatsCardSkeleton key={i} />)}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="h-10 bg-gray-200 rounded-lg mb-4 w-full"></div>
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <TableRowSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="College Management" toggleSidebar={toggleSidebar} />
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-4 max-w-full"
          >
            {/* Stats Cards */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 }
                }
              }}
              className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4"
            >
              {[
                { label: 'Total', value: stats.total, icon: HiOutlineOfficeBuilding },
                { label: 'Pending', value: stats.pending, icon: HiOutlineClock },
                { label: 'Approved', value: stats.approved, icon: HiOutlineCheck },
                { label: 'Rejected', value: stats.rejected, icon: HiOutlineX },
                { label: 'Suspended', value: stats.suspended, icon: HiOutlineBan }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  variants={{
                    hidden: { y: 20, opacity: 0 },
                    visible: { y: 0, opacity: 1 }
                  }}
                  whileHover={{ y: -2 }}
                  className="bg-white rounded-xl shadow-sm p-4 border border-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-xl font-bold text-gray-800">{stat.value}</p>
                    </div>
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <stat.icon className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Search and Filter Bar */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-200"
            >
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by college name, email, admin..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <HiOutlineX className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => fetchColleges(true)}
                    disabled={refreshing}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl border border-gray-300 transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <HiOutlineRefresh className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Results info */}
            <div className="flex justify-between items-center mb-3 px-1">
              <p className="text-sm text-gray-500">
                Showing {paginatedColleges.length} of {filteredColleges.length} colleges
              </p>
              {filteredColleges.length > 0 && (
                <p className="text-xs text-gray-400">
                  Page {currentPage} of {totalPages}
                </p>
              )}
            </div>

            {/* Colleges Table */}
            {filteredColleges.length === 0 ? (
              <EmptyState />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200"
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College</th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Admin</th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedColleges.map((college, index) => (
                        <motion.tr
                          key={college._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 text-sm truncate max-w-[150px] group-hover:text-gray-900">
                                  {college.collegeName}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-[150px] flex items-center gap-1">
                                  <HiOutlineMail className="w-3 h-3" />
                                  {college.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <HiOutlinePhone className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600 truncate max-w-[120px]">
                                {college.contactNumber || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <HiOutlineUser className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                                {college.adminName || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(college.status)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-1">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                  setSelectedCollege(college);
                                  setShowDetailsModal(true);
                                }}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="View details"
                              >
                                <HiOutlineEye className="w-4 h-4" />
                              </motion.button>
                              
                              {college.status === 'suspended' ? (
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleUnsuspend(college._id)}
                                  disabled={processingId === college._id}
                                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
                                  title="Unsuspend"
                                >
                                  <HiOutlineCheck className="w-4 h-4" />
                                </motion.button>
                              ) : (
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => {
                                    setSelectedCollege(college);
                                    setShowSuspendModal(true);
                                  }}
                                  disabled={processingId === college._id}
                                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
                                  title="Suspend"
                                >
                                  <HiOutlineBan className="w-4 h-4" />
                                </motion.button>
                              )}
                              
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleDelete(college._id)}
                                disabled={processingId === college._id}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                                title="Delete"
                              >
                                <HiOutlineTrash className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                      {Math.min(currentPage * itemsPerPage, filteredColleges.length)} of{' '}
                      {filteredColleges.length} colleges
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <HiOutlineChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <HiOutlineChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedCollege && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <HiOutlineOfficeBuilding className="w-5 h-5" />
                  College Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <HiOutlineX className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <HiOutlineOfficeBuilding className="w-8 h-8 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-bold text-gray-800 break-words mb-1">
                      {selectedCollege.collegeName}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <HiOutlineMail className="w-4 h-4" />
                      <span className="break-words">{selectedCollege.email}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlinePhone className="w-3 h-3" />
                      Contact Number
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.contactNumber || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineGlobeAlt className="w-3 h-3" />
                      Website
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.website || 'Not provided'}
                    </p>
                  </div>
                  <div className="col-span-2 bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineLocationMarker className="w-3 h-3" />
                      Address
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.address || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlineUser className="w-3 h-3" />
                      Admin Name
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.adminName || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <HiOutlinePhone className="w-3 h-3" />
                      Admin Phone
                    </p>
                    <p className="font-medium text-gray-800 break-words">
                      {selectedCollege.adminPhone || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    {getStatusBadge(selectedCollege.status)}
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Registered On</p>
                    <p className="font-medium text-gray-800">{formatDate(selectedCollege.createdAt)}</p>
                  </div>
                </div>

                {selectedCollege.suspendReason && (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="text-red-600 font-medium text-sm mb-1 flex items-center gap-1">
                      <HiOutlineBan className="w-4 h-4" />
                      Suspension Reason
                    </p>
                    <p className="text-sm text-red-700 break-words">{selectedCollege.suspendReason}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Suspend Modal */}
      <AnimatePresence>
        {showSuspendModal && selectedCollege && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowSuspendModal(false);
              setSuspendReason('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-2">Suspend College</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Suspend <span className="font-semibold">{selectedCollege.collegeName}</span>
                </p>
                
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension..."
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  rows="3"
                  autoFocus
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuspendModal(false);
                      setSuspendReason('');
                    }}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSuspend}
                    disabled={!suspendReason.trim() || processingId === selectedCollege._id}
                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {processingId === selectedCollege._id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Suspend'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollegeManagement;